// Rich-text serializers for the "Copy email" / "Copy for MSFT Teams"
// buttons. Mirrors the old static hub: a release/feature is turned into an
// email (inline-styled <table>, Outlook/Gmail-safe) or a Teams message
// (plain semantic <div>, since Teams strips tables), plus a plain-text
// fallback for editors that ignore text/html.
//
// Content is derived MECHANICALLY from the structured data we already load:
//   intro   = release narrative / feature announcement-narrative|pitch
//   sections = release items grouped by category, or a feature's
//              web/mobile notes + known gaps + links.
// No hand-authored marketing copy and no LLM call — what's on the card is
// what you copy.
import type { EnrichedFeature, ItemCategory, Release } from "@release-hub/shared";
import { markdownToHtml } from "./markdown";
import { releaseUrl, featureUrl } from "./url";

// ─── Shape ────────────────────────────────────────────────────────────

export interface Section {
  title: string;
  /** Prose paragraph (markdown-ish), e.g. a feature's web/mobile note. */
  body?: string;
  /** Bullet lines (markdown-ish), e.g. a release's items in a category. */
  bullets?: string[];
}

/** Format-agnostic content, ready to render as email/teams/plain. */
export interface Composed {
  subject: string;
  title: string;
  intro: string;
  sections: Section[];
  /** Optional deep link to the release/feature in Release Hub. */
  url?: string;
}

export type ExportFormat = "raw" | "slack" | "email" | "teams";

export interface ExportPayload {
  subject: string;
  title: string;
  /** "html" → rich clipboard (email/Teams). "text" → plain clipboard
   *  (raw/Slack). Drives both the preview surface and the copy path. */
  kind: "html" | "text";
  /** Rendered markup for html kinds; null for text kinds. */
  html: string | null;
  /** Always populated — the payload for text kinds, and the fallback that
   *  rides along with the html for rich kinds. */
  plain: string;
}

// Category grouping mirrors ItemList so the copied output matches the card.
const CATEGORY_ORDER: ItemCategory[] = [
  "feature",
  "enhancement",
  "bugfix",
  "maintenance",
];
const CATEGORY_LABELS: Record<ItemCategory, string> = {
  feature: "New features",
  enhancement: "Enhancements",
  bugfix: "Bugfixes",
  maintenance: "Maintenance",
};

// ─── Compose from entities ────────────────────────────────────────────

export function composeRelease(r: Release): Composed {
  const primaryTag = r.builds?.[0]?.tag_name ?? null;
  const title = r.title?.trim() || primaryTag || "Release";

  const buckets = new Map<ItemCategory, string[]>();
  for (const it of r.items ?? []) {
    if (!it.text?.trim()) continue;
    if (!buckets.has(it.category)) buckets.set(it.category, []);
    buckets.get(it.category)!.push(it.text.trim());
  }
  const sections: Section[] = [];
  for (const cat of CATEGORY_ORDER) {
    const bullets = buckets.get(cat);
    if (bullets?.length) sections.push({ title: CATEGORY_LABELS[cat], bullets });
  }

  return {
    subject: `New in Squint: ${title}`,
    title,
    intro: r.narrative?.trim() ?? "",
    sections,
    url: releaseUrl(r.id),
  };
}

export function composeFeature(f: EnrichedFeature): Composed {
  const title = f.name;
  const intro = (f.announcement_narrative || f.pitch || "").trim();

  const sections: Section[] = [];
  if (f.web_note?.trim()) sections.push({ title: "On web", body: f.web_note.trim() });
  if (f.mobile_note?.trim()) {
    sections.push({ title: "On mobile", body: f.mobile_note.trim() });
  }
  if (f.known_gaps?.length) {
    sections.push({ title: "Known gaps", bullets: f.known_gaps });
  }

  return {
    subject: `New in Squint: ${title}`,
    title,
    intro,
    sections,
    url: featureUrl(f.id),
  };
}

// ─── HTML/text builders ───────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** markdownToHtml, minus the wrapping <p> — for text that belongs inside
 *  another element (e.g. an <li>). Leaves multi-block content untouched. */
function inlineHtml(text: string): string {
  const html = markdownToHtml(text.trim());
  const m = html.match(/^<p>([\s\S]*)<\/p>$/);
  return m ? m[1]! : html;
}

const GREETING = "Hi [First name],";
const SIGN_OFF =
  "Questions or want to dig in deeper? Just reply to this email — we'd love to chat.";
const SIGN_OFF_TEAMS =
  "Questions or want to dig in deeper? Just reply — we'd love to chat.";

function emailHtml(c: Composed): string {
  const intro = c.intro
    ? `<tr><td style="padding:0 0 20px 0;font-size:14px;color:#474747;line-height:1.7">${markdownToHtml(c.intro)}</td></tr>`
    : "";

  const sectionRows = c.sections
    .map((s) => {
      let rows = `<tr><td style="padding:0 0 6px 0;font-size:15px;font-weight:700;color:#171717">${escapeHtml(s.title)}</td></tr>`;
      if (s.body?.trim()) {
        rows += `<tr><td style="padding:0 0 6px 0;font-size:14px;color:#474747;line-height:1.7">${markdownToHtml(s.body.trim())}</td></tr>`;
      }
      if (s.bullets?.length) {
        const lis = s.bullets
          .map((b) => `<li style="margin:0 0 4px 0">${inlineHtml(b)}</li>`)
          .join("");
        rows += `<tr><td style="padding:0"><ul style="margin:0;padding-left:20px;font-size:14px;color:#474747;line-height:1.7">${lis}</ul></td></tr>`;
      }
      rows += `<tr><td style="padding:0 0 16px 0;font-size:0;line-height:0">&nbsp;</td></tr>`;
      return rows;
    })
    .join("\n");

  const linkRow = c.url
    ? `<tr><td style="padding:0 0 16px 0;font-size:13px"><a href="${escapeHtml(c.url)}" style="color:#0066CC;text-decoration:none">View in Release Hub</a></td></tr>`
    : "";

  return `<table width="600" cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#171717;max-width:600px">
  <tr><td style="padding:0 0 6px 0;font-size:13px;color:#474747">${GREETING}</td></tr>
  ${intro}
  ${sectionRows}
  ${linkRow}
  <tr><td style="padding:4px 0 16px 0"><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top:1px solid #E0E0E0;font-size:0;line-height:0">&nbsp;</td></tr></table></td></tr>
  <tr><td style="font-size:13px;color:#474747;line-height:1.7">${SIGN_OFF}</td></tr>
</table>`;
}

function teamsHtml(c: Composed): string {
  let h = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#171717;line-height:1.6">`;
  h += `<p>${GREETING}</p>`;
  if (c.intro) h += markdownToHtml(c.intro);
  for (const s of c.sections) {
    h += `<p><b>${escapeHtml(s.title)}</b></p>`;
    if (s.body?.trim()) h += markdownToHtml(s.body.trim());
    if (s.bullets?.length) {
      h += `<ul>${s.bullets.map((b) => `<li>${inlineHtml(b)}</li>`).join("")}</ul>`;
    }
  }
  if (c.url) {
    h += `<p><a href="${escapeHtml(c.url)}">View in Release Hub</a></p>`;
  }
  h += `<p>${SIGN_OFF_TEAMS}</p>`;
  h += `</div>`;
  return h;
}

/** Plain-text fallback that rides along with the email/Teams rich copy —
 *  keeps the greeting + sign-off chrome so it reads like the message. */
function plainText(c: Composed): string {
  const parts: string[] = [GREETING, ""];
  if (c.intro) parts.push(c.intro, "");
  for (const s of c.sections) {
    parts.push(s.title);
    if (s.body?.trim()) parts.push(s.body.trim());
    if (s.bullets?.length) parts.push(...s.bullets.map((b) => `• ${b}`));
    parts.push("");
  }
  if (c.url) {
    parts.push(`View in Release Hub: ${c.url}`, "");
  }
  parts.push(SIGN_OFF_TEAMS);
  return parts.join("\n");
}

/** Bare content, no message chrome — for pasting into an LLM or a doc. */
function rawText(c: Composed): string {
  const parts: string[] = [c.title, ""];
  if (c.intro) parts.push(c.intro, "");
  for (const s of c.sections) {
    parts.push(s.title);
    if (s.body?.trim()) parts.push(s.body.trim());
    if (s.bullets?.length) parts.push(...s.bullets.map((b) => `- ${b}`));
    parts.push("");
  }
  return `${parts.join("\n").trim()}\n`;
}

/** Convert markdown emphasis/links to Slack's mrkdwn dialect. */
function slackInline(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, "*$1*") // **bold** → *bold*
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "<$2|$1>"); // [t](u) → <u|t>
}

/** Slack mrkdwn — copied as plain text; Slack renders the markers on paste. */
function slackText(c: Composed): string {
  const parts: string[] = [`*${c.title}*`, ""];
  if (c.intro) parts.push(slackInline(c.intro), "");
  for (const s of c.sections) {
    parts.push(`*${s.title}*`);
    if (s.body?.trim()) parts.push(slackInline(s.body.trim()));
    if (s.bullets?.length) {
      parts.push(...s.bullets.map((b) => `• ${slackInline(b)}`));
    }
    parts.push("");
  }
  if (c.url) {
    parts.push(`<${c.url}|View in Release Hub>`);
  }
  return `${parts.join("\n").trim()}\n`;
}

export function buildExport(c: Composed, format: ExportFormat): ExportPayload {
  const base = { subject: c.subject, title: c.title };
  switch (format) {
    case "email":
      return { ...base, kind: "html", html: emailHtml(c), plain: plainText(c) };
    case "teams":
      return { ...base, kind: "html", html: teamsHtml(c), plain: plainText(c) };
    case "slack":
      return { ...base, kind: "text", html: null, plain: slackText(c) };
    case "raw":
    default:
      return { ...base, kind: "text", html: null, plain: rawText(c) };
  }
}
